import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { StatusBadge, FolioBadge, EmptyState } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { money, pad, fmtDateTime, PAYMENT_STATUS } from "@/lib/format";
import { toast } from "sonner";
import {
  ZoomIn, RotateCw, Download, CheckCircle2, XCircle, AlertTriangle, FileWarning, Inbox, ShieldAlert,
} from "lucide-react";

const TABS = [["all", "Todos"], ["in_review", "Nuevos"], ["correction_requested", "Corrección"], ["approved", "Aprobados"], ["rejected", "Rechazados"]];

export default function PaymentValidation() {
  const [sp] = useSearchParams();
  const [filter, setFilter] = useState(sp.get("f") || "in_review");
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [reason, setReason] = useState("");

  const load = () => api.get("/admin/payments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const [proofUrl, setProofUrl] = useState(null);
  const [proofErr, setProofErr] = useState(false);
  useEffect(() => {
    setProofUrl(null); setProofErr(false);
    if (!sel?.proof?.storage_path) return;
    let url;
    api.get(`/admin/proofs/${sel.folio}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setProofUrl(url); })
      .catch(() => setProofErr(true));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [sel]);

  const filtered = items.filter((i) => filter === "all" ? true : i.payment_status === filter);

  const act = async (action) => {
    if (!sel) return;
    if ((action === "reject" || action === "correction") && !reason) { toast.error("Escribe un motivo"); return; }
    try {
      await api.post(`/admin/payments/${sel.folio}/review`, { action, reason, comment: reason });
      toast.success(action === "approve" ? "Pago aprobado, boletos confirmados" : action === "reject" ? "Comprobante rechazado" : "Corrección solicitada");
      setReason(""); setSel(null); await load();
    } catch { toast.error("No se pudo procesar"); }
  };

  const alerts = sel ? (() => {
    const a = [];
    if (sel.proof && Math.abs(sel.proof.amount - sel.total) > 0.5) a.push("Monto reportado distinto al esperado");
    if (!sel.proof) a.push("Sin comprobante adjunto");
    return a;
  })() : [];

  return (
    <AdminLayout title="Centro de validación de pagos">
      <Tabs value={filter} onValueChange={setFilter}>
        <div className="overflow-x-auto pb-1"><TabsList data-testid="payment-tabs">{TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`ptab-${v}`}>{l}</TabsTrigger>)}</TabsList></div>
      </Tabs>

      <div className="mt-6 grid gap-6 lg:grid-cols-[380px_1fr]">
        {/* Inbox */}
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <EmptyState icon={Inbox} title="Bandeja vacía" desc="No hay comprobantes en esta categoría." />
          ) : filtered.map((it) => (
            <button key={it.folio} onClick={() => { setSel(it); setZoom(1); setRot(0); }} data-testid={`payment-item-${it.folio}`}
              className={`w-full rounded-xl border p-4 text-left transition-colors ${sel?.folio === it.folio ? "border-brand bg-blue-50/40" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-bold text-slate-900">{it.folio}</span>
                <StatusBadge {...(PAYMENT_STATUS[it.payment_status] || PAYMENT_STATUS.pending)} />
              </div>
              <div className="mt-1.5 text-sm text-slate-600">{it.customer?.name} {it.customer?.lastname}</div>
              <div className="mt-1 flex items-center justify-between text-xs text-slate-400">
                <span>{it.numbers?.length} boletos · {fmtDateTime(it.created_at)}</span>
                <span className="font-mono font-semibold text-slate-700">{money(it.total)}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Review */}
        {!sel ? (
          <div className="grid place-items-center rounded-xl border border-dashed border-slate-200 bg-white py-24 text-center text-slate-400">
            <div><Inbox className="mx-auto h-10 w-10" /><p className="mt-3 text-sm">Selecciona un comprobante para revisar</p></div>
          </div>
        ) : (
          <div className="grid gap-6 xl:grid-cols-2">
            {/* Left: proof */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">Comprobante</span>
                <div className="flex gap-1">
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setZoom((z) => Math.min(2, z + 0.2))} data-testid="proof-zoom"><ZoomIn className="h-4 w-4" /></Button>
                  <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setRot((r) => r + 90)} data-testid="proof-rotate"><RotateCw className="h-4 w-4" /></Button>
                  <Button asChild size="icon" variant="outline" className="h-8 w-8" data-testid="proof-download"><a href={proofUrl || "#"} download={sel.proof?.filename || "comprobante"} onClick={(e) => { if (!proofUrl) e.preventDefault(); }}><Download className="h-4 w-4" /></a></Button>
                </div>
              </div>
              <div className="grid aspect-[3/4] place-items-center overflow-hidden rounded-lg bg-slate-900">
                {sel.proof && sel.proof.storage_path ? (
                  proofErr ? (
                    <div className="text-center text-slate-400"><FileWarning className="mx-auto h-12 w-12" /><p className="mt-2 text-sm">No se pudo cargar el comprobante</p></div>
                  ) : !proofUrl ? (
                    <div className="text-center text-sm text-slate-400">Cargando comprobante…</div>
                  ) : sel.proof.content_type === "application/pdf" ? (
                    <iframe title="comprobante" src={proofUrl} className="h-full w-full bg-white" data-testid="proof-pdf" />
                  ) : (
                    <img src={proofUrl} alt="Comprobante" data-testid="proof-image"
                      style={{ transform: `scale(${zoom}) rotate(${rot}deg)`, transition: "transform 0.2s" }}
                      className="max-h-full max-w-full object-contain" />
                  )
                ) : sel.proof ? (
                  <div className="grid place-items-center text-center text-white">
                    <FileWarning className="mx-auto h-16 w-16 text-slate-500" />
                    <div className="mt-3 font-mono text-sm">{sel.proof.filename}</div>
                    <div className="mt-1 text-xs text-slate-400">Comprobante heredado (sin archivo almacenado)</div>
                  </div>
                ) : (
                  <div className="text-center text-slate-500"><FileWarning className="mx-auto h-12 w-12" /><p className="mt-2 text-sm">Sin comprobante</p></div>
                )}
              </div>
            </div>

            {/* Right: details + actions */}
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between"><FolioBadge folio={sel.folio} /><StatusBadge {...(PAYMENT_STATUS[sel.payment_status] || PAYMENT_STATUS.pending)} /></div>
                <dl className="mt-4 space-y-2.5 text-sm">
                  <Row k="Cliente" v={`${sel.customer?.name} ${sel.customer?.lastname || ""}`} />
                  <Row k="Correo" v={sel.customer?.email} />
                  <Row k="Sorteo" v={sel.raffle_name} />
                  <div className="border-t border-slate-100 pt-2.5">
                    <dt className="mb-1 text-slate-500">Boletos ({sel.numbers?.length || 0})</dt>
                    <dd className="max-h-32 overflow-y-auto break-all font-mono text-xs leading-relaxed text-slate-900" data-testid="detail-tickets">{sel.numbers?.map((n) => pad(n)).join(", ")}</dd>
                  </div>
                  <Row k="Monto esperado" v={money(sel.total)} mono />
                  <Row k="Monto reportado" v={sel.proof ? money(sel.proof.amount) : "—"} mono />
                  <Row k="Referencia" v={sel.proof?.reference || sel.folio} mono />
                </dl>
              </div>

              {alerts.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  {alerts.map((a) => <div key={a} className="flex items-center gap-2 text-sm font-medium text-amber-800"><AlertTriangle className="h-4 w-4" />{a}</div>)}
                </div>
              )}

              {["in_review", "pending", "correction_requested"].includes(sel.payment_status) ? (
                <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo / comentario (requerido para rechazo o corrección)" rows={2} className="mb-3" data-testid="review-reason" />
                  <div className="grid grid-cols-3 gap-2">
                    <Button onClick={() => act("approve")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-approve"><CheckCircle2 className="mr-1.5 h-4 w-4" />Aprobar</Button>
                    <Button onClick={() => act("correction")} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="btn-correction"><AlertTriangle className="mr-1.5 h-4 w-4" />Corrección</Button>
                    <Button onClick={() => act("reject")} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-reject"><XCircle className="mr-1.5 h-4 w-4" />Rechazar</Button>
                  </div>
                  <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-400"><ShieldAlert className="h-3.5 w-3.5" />Cada acción queda registrada en auditoría con usuario, fecha e IP.</p>
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-500">Operación en estado final: <b>{(PAYMENT_STATUS[sel.payment_status] || {}).label}</b></div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

const Row = ({ k, v, mono }) => (
  <div className="flex justify-between gap-4"><dt className="shrink-0 text-slate-500">{k}</dt><dd className={`min-w-0 break-words text-right font-medium text-slate-900 ${mono ? "font-mono" : ""}`}>{v}</dd></div>
);
